/**
 * Composer draft store — a programmatic channel for prefilling a task's message
 * composer. A feature (e.g. Studio's Retry-a-failed-generation) calls
 * `requestPrefill(taskId, text)`; the task layout reads the per-task draft, pushes
 * its text + mode into the composer's `prefillText` / `prefillMode`, and calls
 * `consumePrefill` so the same draft never re-fires. Keyed per task so a
 * background task can't prefill the one on screen.
 *
 * A draft carries a `mode`: `replace` swaps the composer (Retry / Remix / Upscale
 * / Animate), `append` extends the composer's LIVE text (Studio's "Set as ref").
 * The append merge runs at inject time against the live text (see MessageInput's
 * `applyPrefill`), NOT here — the store only records the intent.
 */

import { create } from "zustand";

export type PrefillMode = "replace" | "append";

export interface ComposerDraft {
  text: string;
  mode: PrefillMode;
}

/** A file someone shared with this person, handed to the home composer by "Use with Bash" (Files, Shared with me). */
export interface SharedFileRef {
  fileId: string;
  filename: string;
  mediaType: string;
  sizeBytes: number;
}

interface ComposerDraftStore {
  prefillByTask: Record<string, ComposerDraft | undefined>;
  /** Prefill that replaces the composer's contents. */
  requestPrefill: (taskId: string, text: string) => void;
  /** Prefill that appends to the composer's live text (Studio's "Set as ref"). */
  appendPrefill: (taskId: string, text: string) => void;
  consumePrefill: (taskId: string) => void;
  /** Home-dock lane: the first-run starters write here (home has no taskId to
   *  key by); the Dock consumes on next render and focuses its input. */
  homePrefill: string | null;
  prefillHome: (text: string) => void;
  consumeHomePrefill: () => void;
  /** The home composer's unsent words, parked here so the Line's type door
   *  (spec 2026-09-11) can close over them and open again on them; the letter
   *  that opens the door is appended here too (liveStore.openDoor). */
  homeDraft: string;
  parkHomeDraft: (text: string) => void;
  /** Files dropped on the ball on /home (the Dock's stage), parked for the
   *  home composer's own attachment lane; taken once, on its mount. */
  homeFiles: File[] | null;
  parkHomeFiles: (files: File[]) => void;
  takeHomeFiles: () => File[] | null;
  /** Files handed over from Shared with me ("Use with Bash"), parked for the home composer's
   *  attachment lane like homeFiles; they are already stored, so they ride by id (their /v door).
   *  Parking adds to what is parked (an unsent ref parked back survives the next hand-over);
   *  the lane keeps one row per file. */
  homeRefs: SharedFileRef[] | null;
  parkHomeRefs: (refs: SharedFileRef[]) => void;
  takeHomeRefs: () => SharedFileRef[] | null;
}

export const useComposerDraftStore = create<ComposerDraftStore>((set, get) => ({
  prefillByTask: {},
  homePrefill: null,
  prefillHome: (text) => set({ homePrefill: text }),
  consumeHomePrefill: () =>
    set((s) => (s.homePrefill === null ? s : { homePrefill: null })),
  homeDraft: "",
  parkHomeDraft: (text) => set({ homeDraft: text }),
  homeFiles: null,
  parkHomeFiles: (files) => set({ homeFiles: files }),
  takeHomeFiles: () => {
    const files = get().homeFiles;
    if (files !== null) set({ homeFiles: null });
    return files;
  },
  homeRefs: null,
  parkHomeRefs: (refs) =>
    set((s) => ({ homeRefs: [...(s.homeRefs ?? []), ...refs] })),
  takeHomeRefs: () => {
    const refs = get().homeRefs;
    if (refs !== null) set({ homeRefs: null });
    return refs;
  },
  requestPrefill: (taskId, text) =>
    set((s) => ({
      prefillByTask: {
        ...s.prefillByTask,
        [taskId]: { text, mode: "replace" },
      },
    })),
  appendPrefill: (taskId, text) =>
    set((s) => ({
      prefillByTask: { ...s.prefillByTask, [taskId]: { text, mode: "append" } },
    })),
  consumePrefill: (taskId) =>
    set((s) => {
      if (s.prefillByTask[taskId] === undefined) return s;
      const next = { ...s.prefillByTask };
      delete next[taskId];
      return { prefillByTask: next };
    }),
}));
